// One copy of the source video, fetched a chunk at a time in the order the
// viewer needs them.
//
// The bytes were already being downloaded: services/audioWaveform pulls the
// whole file (169.9MB on the measured source, ~117s) and the filmstrip pulled
// keyframes out of it separately — 189MB between them, for a strip still 1.92s
// out. This makes it one download, shared, and once the bytes are here nothing
// about a frame is approximate: measured in Chrome, the whole 23941-frame file
// decodes in 8.9s at 2703 fps and any single frame is 82ms.
//
// Order is the point. A stream from byte 0 hands over the front of the video
// first whatever the viewer is looking at, so the stretch they are watching
// fills in last and jumps when it does. Chunks are cut at keyframes — the
// smallest piece that decodes on its own — and pulled nearest-the-viewport
// first, re-sorted whenever the view moves.

import { createByteStore } from '@src/services/byteStore';
import { orderChunks } from '@src/services/chunkPlan';
import { fetchRange, normalizeCacheKey } from '@src/services/mediaExtractor';

// Past this the file is not worth pulling into the browser whole. Matches the
// waveform's own ceiling, since they share the download.
export const MAX_MEDIA_FILE_BYTES = 1024 * 1024 * 1024;

// Enough requests in flight to hide the round trips, few enough that a zoom
// mid-download has only a couple of chunks' worth of work already committed to
// the wrong place.
const MAX_PARALLEL_CHUNKS = 3;

// One video at a time: the file is the largest thing the page holds and the
// timeline only ever shows one.
const files = new Map();
// Callers that want the finished file before anyone has started fetching it.
const earlyReaders = new Map();

const startFile = (url, chunks, totalSize) => {
  const store = createByteStore();
  const held = new Set();
  const listeners = new Set();
  const inFlight = new Set();
  // Chunks whose frames a caller already had from cache. Still fetched, because
  // the file is only whole once every byte is here and the waveform needs it
  // whole — but fetched last, behind everything that would change the picture.
  const deferred = new Set();
  const controller = new AbortController();
  let stopped = false;

  const entry = {
    store,
    held,
    listeners,
    chunks,
    totalSize,
    holders: 0,
    ready: false,
    deferred,
    // What the viewer is looking at. Every pull reads this, so moving the view
    // re-orders everything not already in flight.
    view: { fromSec: 0, toSec: Infinity, needed: [] },
  };

  let settle = () => {};
  let fail = () => {};
  entry.done = new Promise((resolve, reject) => {
    settle = resolve;
    fail = reject;
  });
  // `done` exists from the moment the download starts, whether or not anyone is
  // waiting on it — and dropping the download now rejects it. Without a handler
  // of its own, switching away from a video nobody happened to be reading would
  // surface as an unhandled rejection.
  entry.done.catch(() => {});

  const nextChunk = () => {
    const taken = (chunk, index) => held.has(index) || inFlight.has(index);
    const wanted = orderChunks(chunks, {
      fromSec: entry.view.fromSec,
      toSec: entry.view.toSec,
      needed: entry.view.needed,
      isHeld: (chunk, index) => taken(chunk, index) || deferred.has(index),
    });
    if (wanted.length) return wanted[0];
    // Only what was deferred is left; take it front to back.
    return orderChunks(chunks, {
      fromSec: 0,
      toSec: Infinity,
      isHeld: taken,
    })[0];
  };

  let pump = () => {};

  const take = (next) => {
    inFlight.add(next);
    const chunk = chunks[next];
    fetchRange(url, chunk.start, chunk.end - 1, controller.signal)
      .then((buffer) => {
        inFlight.delete(next);
        if (stopped) return;
        store.add(chunk.start, new Uint8Array(buffer));
        held.add(next);
        listeners.forEach((listener) => listener(next, chunk));
        pump();
      })
      .catch((error) => {
        inFlight.delete(next);
        if (stopped) return;
        stopped = true;
        fail(error);
      });
  };

  pump = () => {
    if (stopped) return;
    if (held.size === chunks.length) {
      if (entry.ready) return;
      entry.ready = true;
      settle(store.toBlob());
      return;
    }
    while (inFlight.size < MAX_PARALLEL_CHUNKS) {
      const next = nextChunk();
      if (next === undefined) return;
      take(next);
    }
  };

  entry.pump = pump;
  // Dropping the download has to let go of everyone waiting on it. The abort
  // rejects the fetches, but `take` swallows that once `stopped` is set — so
  // nothing settles `done` and every reader waits for a file that will never
  // arrive. The waveform is one of those readers, and it holds one of three
  // shared decode slots while it waits: three video switches and no clip
  // anywhere on the timeline decodes again until the page is reloaded.
  entry.stop = () => {
    stopped = true;
    controller.abort();
    fail(new Error('media file dropped'));
  };
  return entry;
};

/**
 * Join the download of `url`, starting it if nobody has.
 *
 * `chunks` (from planChunks) says where the pieces are; the first caller decides
 * them and later ones reuse whatever is already running. `onChunk(index, chunk)`
 * fires as each lands. Returns null when nothing is running and the caller
 * brought no plan of its own.
 *
 * Callers must `release()`. The download stops only once the last holder has let
 * go and it has not finished — one track unmounting must not cancel what another
 * is reading, and a finished file is kept, because it IS the copy.
 */
export const joinMediaFile = (url, { chunks, totalSize, onChunk } = {}) => {
  const key = normalizeCacheKey(url);
  let entry = files.get(key);
  if (!entry) {
    if (!chunks || !chunks.length) return null;
    files.forEach((other, otherKey) => {
      if (otherKey === key) return;
      other.stop();
      files.delete(otherKey);
    });
    entry = startFile(url, chunks, totalSize);
    files.set(key, entry);
    const waiting = earlyReaders.get(key);
    if (waiting) {
      earlyReaders.delete(key);
      waiting.forEach((resolve) => resolve(entry));
    }
  }
  entry.holders += 1;
  if (onChunk) entry.listeners.add(onChunk);

  let released = false;
  return {
    store: entry.store,
    held: entry.held,
    chunks: entry.chunks,
    done: entry.done,

    /** Where the viewer is looking; the next pull orders around it. */
    setView: (fromSec, toSec, needed = []) => {
      entry.view = { fromSec, toSec, needed };
      entry.pump();
    },

    /** This chunk's frames are already in hand — fetch it last, not never. */
    deferChunk: (index) => entry.deferred.add(index),

    start: () => entry.pump(),

    release: () => {
      if (released) return;
      released = true;
      if (onChunk) entry.listeners.delete(onChunk);
      entry.holders -= 1;
      if (entry.holders <= 0 && !entry.ready) {
        entry.stop();
        files.delete(key);
      }
    },
  };
};

// How long a caller with no plan of its own waits for one to appear before
// giving up and reading the file its own way. The filmstrip starts within a
// couple of round trips of mount; past this it is not going to.
const EARLY_READER_TIMEOUT_MS = 15000;

const entrySize = (key) => files.get(key)?.totalSize || 0;

/**
 * The whole file as a Blob, joining whatever download is running — what the
 * waveform uses instead of fetching the file a second time. Waits for every
 * chunk, since decoding audio needs the file whole.
 *
 * Resolves null when there is no download to join, which tells the caller to go
 * and get the bytes itself.
 *
 * REJECTS when a download that had started is dropped part way, which is a
 * different answer: the video has been switched away from, so the caller should
 * give up rather than fetch the file for itself. Returning null there would send
 * the waveform off to download 170MB of a video that is no longer on screen —
 * and it would go through with it, since useAudioWaveform deliberately carries
 * no abort signal (its decode is shared between clips).
 */
export const readMediaFile = async (url) => {
  const key = normalizeCacheKey(url);
  if (!files.has(key)) {
    const appeared = await new Promise((resolve) => {
      const waiting = earlyReaders.get(key) || [];
      waiting.push(resolve);
      earlyReaders.set(key, waiting);
      setTimeout(() => resolve(null), EARLY_READER_TIMEOUT_MS);
    });
    if (!appeared) return null;
  }
  if (entrySize(key) > MAX_MEDIA_FILE_BYTES) return null;
  const handle = joinMediaFile(url);
  if (!handle) return null;
  try {
    return await handle.done;
  } finally {
    handle.release();
  }
};
